import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Box, Card, ElementContainer, SideDrawer, TextField, withStyles } from 'vopay';
import { Button, Skeleton, UtilButton } from '#shared/components/UI';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useToast } from '#shared/providers/Toast';
import useUsers from '../useUsers';
import PermissionsSearchField from '../UserPermissionsDrawer/PermissionsSearchField';
import UserPermissionsForm, {
  PermissionFormValues,
  UserPermissionsFormRef,
} from '../UserPermissionsDrawer/UserPermissionsForm';
import RoleCheckbox from './RoleCheckbox';
import useCreateRole from './useCreateRole';
import useRolePermissionsCatalog from './useRolePermissionsCatalog';
import styles from './CreateRoleDrawer.module.scss';

const cn = withStyles(styles);

const CREATE_ROLE_FORM_ID = 'createRolePermissionsForm';

type CreateRoleDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Fired after the role is created, with its identifiers so a caller can chain a follow-up (e.g.
   * assigning it to the user the admin was editing).
   */
  onRoleCreated: (newRole: { RoleID: number; Name: string }) => Promise<void>;
  /** Suppress the drawer's own success toast when the caller shows a combined one. Errors still show. */
  suppressSuccessToast?: boolean;
};

type Step = 'name' | 'permissions';

export default function CreateRoleDrawer({
  isOpen,
  onClose,
  onRoleCreated,
  suppressSuccessToast = false,
}: CreateRoleDrawerProps) {
  const { account, createRoleInSubAccounts } = useUsers();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.CreateRoleDrawer' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const { t: tErrors } = useTranslation('translations', { keyPrefix: 'Shared.Errors' });
  const blockingLoader = useBlockingLoader();
  const toast = useToast();
  const alertDialog = useAlertDialog();
  const { createRole } = useCreateRole();
  const { catalog, isFetching: isFetchingCatalog, fetchCatalog } = useRolePermissionsCatalog();
  const formRef = useRef<UserPermissionsFormRef | null>(null);
  const [step, setStep] = useState<Step>('name');
  const [roleName, setRoleName] = useState('');
  const [isApplyingToAllSubaccounts, setIsApplyingToAllSubaccounts] = useState(false);
  const [hasAtLeastOnePermission, setHasAtLeastOnePermission] = useState(false);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  const [isAllSelected, setIsAllSelected] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setStep('name');
    setRoleName('');
    setIsApplyingToAllSubaccounts(false);
    setHasAtLeastOnePermission(false);
    setPermissionSearchTerm('');
  }, [isOpen]);

  // The catalog is the same for every role, so it is fetched once, the first time the permissions step opens.
  useEffect(() => {
    if (step !== 'permissions' || Object.keys(catalog).length > 0) return;

    fetchCatalog().catch(() => {
      toast.neutral(t('catalogLoadError', { defaultValue: 'Unable to load permissions.' }));
    });
  }, [step]);

  async function handleCreate(values: PermissionFormValues) {
    const rolePayload = {
      Name: roleName.trim(),
      Permissions: Object.entries(values)
        .filter(([, isSelected]) => isSelected)
        .map(([permissionKey]) => permissionKey),
      ApplyToAllSubaccounts: isApplyingToAllSubaccounts,
    };

    blockingLoader.show();

    try {
      const createdRole = await createRole(rolePayload);

      // The backend doesn't propagate to sub-accounts, so a role for all of them is created in each.
      const fanOut =
        isApplyingToAllSubaccounts && createRoleInSubAccounts ? await createRoleInSubAccounts(rolePayload) : null;

      if (fanOut && fanOut.failed.length > 0) {
        toast.neutral(
          t('partialSubaccountPropagation', {
            defaultValue:
              'Role "{{roleName}}" created. Applied to {{numCreated}} of {{numTotal}} sub-accounts ({{numFailed}} failed).',
            roleName: rolePayload.Name,
            numCreated: fanOut.numCreated,
            numTotal: fanOut.numTotal,
            numFailed: fanOut.failed.length,
          }),
        );
      } else if (!suppressSuccessToast) {
        toast.success(
          fanOut
            ? t('subaccountPropagationSuccess', {
                defaultValue: 'Role "{{roleName}}" created and applied to {{numSubAccounts}} sub-account(s).',
                roleName: rolePayload.Name,
                numSubAccounts: fanOut.numTotal,
              })
            : t('successMessage', {
                defaultValue: 'Role "{{roleName}}" was created successfully.',
                roleName: rolePayload.Name,
              }),
        );
      }

      await onRoleCreated({ RoleID: createdRole.RoleID, Name: createdRole.Name });
      onClose();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : tErrors('unknownError', { defaultValue: 'Unknown error' });

      alertDialog.error(t('errorTitle', { defaultValue: 'Could not create role' }), errorMessage);
    } finally {
      blockingLoader.hide();
    }
  }

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Box flx="col" gapSize="12px">
          <span>
            {step === 'name'
              ? t('Step1.title', { defaultValue: 'Create Role' })
              : t('Step2.title', { defaultValue: 'Role Permissions' })}
          </span>

          {step === 'permissions' && (
            <BodyText weight="md">
              {t('Step2.subtitle', {
                defaultValue: 'Configure the permissions for {{roleName}} role.',
                roleName,
              })}
            </BodyText>
          )}
        </Box>
      }
      headerFullWidthContent={
        step === 'permissions' ? (
          <div className={cn('headerSearchRow')}>
            <PermissionsSearchField
              name="rolePermissionSearch"
              value={permissionSearchTerm}
              onChange={setPermissionSearchTerm}
              placeholder={t('Step2.searchPlaceholder', { defaultValue: 'Search Permissions' })}
            />

            <UtilButton
              size="medium"
              onClick={() => formRef.current?.toggleAll(!isAllSelected)}
              className={cn('toggleAllButton')}
            >
              {isAllSelected
                ? t('Step2.disableAll', { defaultValue: 'Disable All' })
                : t('Step2.enableAll', { defaultValue: 'Enable All' })}
            </UtilButton>
          </div>
        ) : undefined
      }
      footer={
        <Box className={cn('footer')}>
          {step === 'name' ? (
            <Button colorScheme="primary" disabled={!roleName.trim()} onClick={() => setStep('permissions')}>
              {tButtons('continue', { defaultValue: 'Continue' })}
            </Button>
          ) : (
            <>
              <Button colorScheme="secondary" onClick={() => setStep('name')}>
                {tButtons('back', { defaultValue: 'Back' })}
              </Button>

              <Button
                colorScheme="primary"
                disabled={!hasAtLeastOnePermission || isFetchingCatalog}
                form={CREATE_ROLE_FORM_ID}
                type="submit"
              >
                {t('Step2.create', { defaultValue: 'Create Role' })}
              </Button>
            </>
          )}
        </Box>
      }
    >
      {step === 'name' && (
        <div className={cn('body')}>
          <Card hoverEffect={false}>
            <Box flx="col" gapSize="24px">
              <ElementContainer
                label={t('Step1.roleName', { defaultValue: 'Role name' })}
                required
                htmlFor="roleName"
                padding="0 0 6px"
                input={
                  <TextField
                    name="roleName"
                    placeholder={t('Step1.roleNamePlaceholder', { defaultValue: 'e.g. Admin' })}
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    onBlur={(e) => setRoleName(e.target.value.trim())}
                    variant="outlineShadow"
                  />
                }
              />

              {account.hasSubAccounts && (
                <RoleCheckbox
                  name="applyToAllSubaccounts"
                  checked={isApplyingToAllSubaccounts}
                  onChange={setIsApplyingToAllSubaccounts}
                  label={t('Step1.applyToAllSubaccounts', {
                    defaultValue: 'Create this role for all existing and future Sub-Accounts',
                  })}
                />
              )}
            </Box>
          </Card>
        </div>
      )}

      {step === 'permissions' && (
        <div className={cn('body')}>
          {isFetchingCatalog && (
            <>
              <Skeleton height="60px" />
              <Skeleton height="60px" />
              <Skeleton height="60px" />
            </>
          )}

          {!isFetchingCatalog && Object.keys(catalog).length > 0 && (
            <UserPermissionsForm
              ref={formRef}
              permissions={catalog}
              id={CREATE_ROLE_FORM_ID}
              searchTerm={permissionSearchTerm}
              onSubmit={handleCreate}
              onFormStateChange={(_, isValid) => setHasAtLeastOnePermission(isValid)}
              onAllEnabledChange={setIsAllSelected}
            />
          )}
        </div>
      )}
    </SideDrawer>
  );
}
